import { useEffect, useState } from "react";
import { shopModeApi } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";

interface Props {
  /** Either an item id (kind="item") or a template id (kind="template"). */
  id: string;
  imageKey: string | null;
  alt: string;
  kind?: "item" | "template";
  /** G-116: "full" for a large view (items only; templates have one size). */
  size?: "thumb" | "full";
}

/**
 * Loads a product / template photo using the floor token (JWT₃) and renders it
 * to fill its container. Falls back to a "photo" placeholder when there is no
 * image or the fetch fails. Revokes the object URL on unmount.
 */
export function ShopFloorBouquetThumb({
  id,
  imageKey,
  alt,
  kind = "item",
  size = "thumb",
}: Props) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!imageKey) {
      setUrl(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    const loader =
      kind === "template"
        ? shopModeApi.floorBouquetTemplateImageBlobUrl(id)
        : shopModeApi.floorItemImageBlobUrl(id, { size, version: imageKey });
    void loader.then((blobUrl) => {
      if (cancelled) {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        return;
      }
      if (!blobUrl) {
        setFailed(true);
        return;
      }
      objectUrl = blobUrl;
      setUrl(blobUrl);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, imageKey, kind, size]);

  if (url) {
    return <img className="sf-bouquet-thumb-img" src={url} alt={alt} loading="lazy" decoding="async" />;
  }
  return (
    <div className="sf-bouquet-thumb-empty" aria-hidden="true">
      {failed || !imageKey ? t("photoInput.photo") : ""}
    </div>
  );
}
